import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Percent, Euro, ShieldCheck, Info, Building2, AlertTriangle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useReloadBus } from "@/lib/reloadBus";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

// Iter88zat (2026-02-19) — Transparenz-Karte für die effektive Marge des
// eingeloggten Users. Löst den wiederkehrenden „Provision reagiert nicht"-Bug:
// User-eigene Overrides überstimmen den Mandanten-Standard leise, sodass
// PV-Katalog- und Paketpreise scheinbar nicht auf Tenant-Änderungen reagieren.
function EffectiveMarginCard({ onCleared }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/tenant-pricing/effective-margin");
      setData(r.data);
    } catch (e) {
      // silent — Karte ausblenden wenn Endpoint fehlt
      setData(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Iter88zbt — nach jedem Save irgendwo auf der Seite (Marge, Rabatt-Caps,
  // Manager-Override, Vertriebler-Override) triggert der ReloadBus dieses
  // Karten-Reload → Anzeige „Effektive Marge" ist immer aktuell.
  // Iter88zbu — Silent (kein Karten-Content-Flash beim Auto-Reload).
  useReloadBus("*", () => load(true));

  const clearOverride = async () => {
    if (!window.confirm("Deine persönliche Provision-Übersteuerung löschen? Danach greift wieder Manager/Mandanten-Standard.")) return;
    setBusy(true);
    try {
      const r = await api.delete("/tenant-pricing/my-user-margin");
      setData(r.data);
      toast.success("Persönliche Übersteuerung entfernt");
      onCleared?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-6 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> Lade Marge-Übersicht…
      </div>
    );
  }
  if (!data) return null;

  const fmtM = (m) => m ? (m.type === "percent" ? `${m.value} %` : `${fmtEur(m.value)} pauschal`) : "—";
  const managerLabel = data.manager_override?.manager_role_label || "Vertriebsleiter";
  const sourceLabel = {
    user_override: "Deine persönliche Übersteuerung",
    sales_manager: `${managerLabel}${data.manager_override?.manager_name ? ` (${data.manager_override.manager_name})` : ""}`,
    sales_director: `Vertriebsdirektor${data.director_override?.director_name ? ` (${data.director_override.director_name})` : ""}`,
    branch_default: `Niederlassung${data.branch_default?.branch_name ? ` ${data.branch_default.branch_name}` : ""}`,
    tenant_default: "Mandanten-Standard",
    none: "Keine Marge gesetzt",
    unknown: "Unklar",
  }[data.source];

  const shadowed = data.is_shadowed_by_override;

  return (
    <div
      className={`rounded-2xl p-5 mb-6 border ${shadowed ? "bg-amber-500/5 border-amber-500/30" : "bg-[#0F1E18] border-[#00E575]/25"}`}
      data-testid="effective-margin-card"
    >
      <div className="flex items-start gap-3">
        <div className={`rounded-full h-9 w-9 flex items-center justify-center shrink-0 ${shadowed ? "bg-amber-500/20 text-amber-300" : "bg-[#00E575]/15 text-[#00E575]"}`}>
          {shadowed ? <AlertTriangle size={17} /> : <Sparkles size={17} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-300">
              Effektive Marge (deine Ansicht)
            </span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${shadowed ? "bg-amber-500/25 text-amber-300" : "bg-[#00E575]/20 text-[#00E575]"}`}>
              {sourceLabel}
            </span>
          </div>
          <div className={`font-mono text-3xl font-bold mt-1 ${shadowed ? "text-amber-200" : "text-[#00E575]"}`} data-testid="effective-margin-value">
            {fmtM(data.effective)}
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 text-[12px]">
            <div className={`rounded-lg px-3 py-2 border ${data.source === "user_override" ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-zinc-900 border-zinc-800"}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold truncate" title="Deine Übersteuerung">Deine Übersteuerung</div>
              <div className="font-mono font-bold text-zinc-100">{fmtM(data.user_override)}</div>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${data.source === "sales_manager" ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-zinc-900 border-zinc-800"}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold truncate" title={managerLabel + (data.manager_override?.manager_name ? ` (${data.manager_override.manager_name})` : "")}>{managerLabel}</div>
              <div className="font-mono font-bold text-zinc-100">{fmtM(data.manager_override)}</div>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${data.source === "sales_director" ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-zinc-900 border-zinc-800"}`} data-testid="director-override-tile">
              <div
                className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold truncate"
                title={"Vertriebsdirektor" + (data.director_override?.director_name ? ` (${data.director_override.director_name})` : "")}
              >
                Vertriebsdirektor{data.director_override?.director_name ? ` (${data.director_override.director_name})` : ""}
              </div>
              <div className="font-mono font-bold text-zinc-100">{fmtM(data.director_override)}</div>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${data.source === "branch_default" ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-zinc-900 border-zinc-800"}`}>
              <div
                className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold truncate"
                title={"Niederlassung" + (data.branch_default?.branch_name ? ` ${data.branch_default.branch_name}` : "")}
              >
                Niederlassung{data.branch_default?.branch_name ? ` ${data.branch_default.branch_name}` : ""}
              </div>
              <div className="font-mono font-bold text-zinc-100">{fmtM(data.branch_default)}</div>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${data.source === "tenant_default" ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-zinc-900 border-zinc-800"}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold truncate" title="Mandanten-Standard">Mandanten-Standard</div>
              <div className="font-mono font-bold text-zinc-100">{fmtM(data.tenant_default)}</div>
            </div>
          </div>
          {shadowed && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[12.5px] text-amber-100 leading-relaxed">
              <p className="font-semibold mb-1">Warum reagieren PV-Preise nicht auf Mandanten-Änderungen?</p>
              <p>
                {data.source === "user_override" && <>Deine <b>persönliche</b> Provision überstimmt den Mandanten-Standard.</>}
                {data.source === "sales_manager" && <>Der Provisions-Satz deines <b>{managerLabel}s</b> überstimmt den Mandanten-Standard für dich.</>}
                {data.source === "sales_director" && <>Der Provisions-Satz deines <b>Vertriebsdirektors</b> überstimmt den Mandanten-Standard für dich.</>}
                {" "}Änderungen unten am
                {" "}<b>Mandanten-Standard</b>{" "}
                wirken sich <b>nicht</b> auf deine Katalog-/Paketpreise aus, sondern nur auf Verkäufer OHNE eigene Übersteuerung.
              </p>
              {data.user_override && (
                <button
                  onClick={clearOverride}
                  disabled={busy}
                  data-testid="clear-my-user-margin-btn"
                  className="mt-3 h-9 px-4 rounded-lg bg-amber-500 text-[#09090B] font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-amber-400 disabled:opacity-50"
                >
                  <X size={14} /> Meine persönliche Übersteuerung löschen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsForm({ target, onSaved, isOwn, hasMultipleTenants, activeTenantName, readOnly = false }) {
  const [marginType, setMarginType] = useState(target.margin_type || "percent");
  const [marginValue, setMarginValue] = useState(target.margin_value ?? "");
  const [maxPct, setMaxPct] = useState(target.max_discount_percent ?? "");
  const [maxEur, setMaxEur] = useState(target.max_discount_eur ?? "");
  const [saving, setSaving] = useState(false);
  // Iter88zay (2026-02-19) — Per-Tenant Scope: nur relevant wenn User mehrere
  // Memberships hat. Standardmäßig „global" (Legacy-Verhalten).
  const [scope, setScope] = useState("global");

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        margin_type: marginValue === "" || marginValue == null ? null : marginType,
        margin_value: marginValue === "" || marginValue == null ? null : parseFloat(marginValue),
        max_discount_percent: maxPct === "" || maxPct == null ? null : parseFloat(maxPct),
        max_discount_eur: maxEur === "" || maxEur == null ? null : parseFloat(maxEur),
      };
      const r = await api.patch(`/users/${target.user_id}/sales-settings?scope=${scope}`, body);
      toast.success(scope === "tenant" ? `Gespeichert (nur ${activeTenantName})` : "Gespeichert");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <fieldset className="space-y-5" disabled={readOnly} style={{ opacity: readOnly ? 0.65 : 1 }}>
      {isOwn && hasMultipleTenants && (
        <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/30 p-3" data-testid="scope-selector">
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-emerald-300 mb-1.5">Wirkungsbereich</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className={`flex items-start gap-2 cursor-pointer rounded-md p-2 border ${scope === "global" ? "bg-emerald-500/10 border-emerald-500/50" : "border-zinc-700"}`}>
              <input type="radio" name="scope" checked={scope === "global"} onChange={() => setScope("global")} data-testid="scope-global" className="mt-0.5" />
              <span className="text-[12px] leading-snug">
                <b className="text-white block">Alle deine Mandanten</b>
                Standard — gilt für jeden Tenant in dem du Mitglied bist.
              </span>
            </label>
            <label className={`flex items-start gap-2 cursor-pointer rounded-md p-2 border ${scope === "tenant" ? "bg-emerald-500/10 border-emerald-500/50" : "border-zinc-700"}`}>
              <input type="radio" name="scope" checked={scope === "tenant"} onChange={() => setScope("tenant")} data-testid="scope-tenant" className="mt-0.5" />
              <span className="text-[12px] leading-snug">
                <b className="text-white block">Nur {activeTenantName || "aktueller Mandant"}</b>
                Andere Mandanten bleiben unverändert. User-Standard unangetastet.
              </span>
            </label>
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-[#00E575]" />
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-200">
            {isOwn ? "Eigene Standard-Provision" : "Provision (überschreibt Standard)"}
          </span>
        </div>
        <p className="text-[12px] text-zinc-300 mb-3">
          Wird automatisch auf alle Paketpreise aufgeschlagen. Für Vertrieb und Kunden unsichtbar.
        </p>
        <div className="flex gap-2 mb-2">
          <button
            data-testid={`margin-type-percent-${target.user_id}`}
            onClick={() => setMarginType("percent")}
            className={`flex-1 h-10 rounded-lg text-sm font-medium border transition flex items-center justify-center gap-1.5 ${
              marginType === "percent"
                ? "border-[#00E575] bg-[#00E575]/15 text-[#00E575]"
                : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-600"
            }`}
          >
            <Percent size={14} /> Prozent
          </button>
          <button
            data-testid={`margin-type-eur-${target.user_id}`}
            onClick={() => setMarginType("eur")}
            className={`flex-1 h-10 rounded-lg text-sm font-medium border transition flex items-center justify-center gap-1.5 ${
              marginType === "eur"
                ? "border-[#00E575] bg-[#00E575]/15 text-[#00E575]"
                : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-600"
            }`}
          >
            <Euro size={14} /> Euro
          </button>
        </div>
        <div className="relative">
          <input
            data-testid={`margin-value-${target.user_id}`}
            type="number"
            min="0"
            step={marginType === "percent" ? "0.5" : "10"}
            value={marginValue}
            onChange={(e) => setMarginValue(e.target.value)}
            placeholder={marginType === "percent" ? "z. B. 15" : "z. B. 1500"}
            className="h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 pr-12 text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
            {marginType === "percent" ? "%" : "€"}
          </span>
        </div>
        <p className="text-[12px] text-zinc-400 mt-1">Leer lassen → keine Marge / Standard erben.</p>
      </div>

      {/* Discount Caps */}
      <div className="pt-5 border-t border-zinc-800">
        <div className="flex items-center gap-2 mb-2">
          <Info size={16} className="text-amber-400" />
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-200">
            Rabatt-Obergrenze pro Angebot
          </span>
        </div>
        <p className="text-[12px] text-zinc-300 mb-3">
          Der Vertrieb darf pro Angebot maximal diesen Rabatt einräumen. Bei beiden Feldern gilt das
          jeweils <span className="text-amber-300 font-semibold">kleinere</span> als Obergrenze.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Max. %</label>
            <div className="relative">
              <input
                data-testid={`max-discount-pct-${target.user_id}`}
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={maxPct}
                onChange={(e) => setMaxPct(e.target.value)}
                placeholder="z. B. 5"
                className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 pr-8 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 text-xs">%</span>
            </div>
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Max. €</label>
            <div className="relative">
              <input
                data-testid={`max-discount-eur-${target.user_id}`}
                type="number"
                min="0"
                step="50"
                value={maxEur}
                onChange={(e) => setMaxEur(e.target.value)}
                placeholder="z. B. 500"
                className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 pr-8 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 text-xs">€</span>
            </div>
          </div>
        </div>
      </div>

      {readOnly ? (
        <div className="mt-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-3 text-[13px]" data-testid={`sales-settings-readonly-${target.user_id}`}>
          <b>Nur Ansicht.</b> Provisions-Werte können nur von Admin oder Super-Admin geändert werden. Bitte an die Geschäftsführung wenden.
        </div>
      ) : (
      <Button
        data-testid={`sales-settings-save-${target.user_id}`}
        onClick={save}
        disabled={saving}
        className="h-12 w-full bg-[#00E575] text-[#09090B] font-semibold rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition"
      >
        {saving ? <Loader2 className="animate-spin" /> : <><Save size={16} className="mr-2" /> Speichern</>}
      </Button>
      )}
    </fieldset>
    {/* Iter88zz7-fix: Historie liegt AUSSERHALB des fieldset, damit
        Vertriebsleiter/Direktor sie öffnen können, auch wenn ihr
        fieldset disabled ist. */}
    <CommissionHistoryBlock userId={target.user_id} />
    </>
  );
}

function CommissionHistoryBlock({ userId }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/users/${userId}/sales-settings/history?limit=30`);
      setEntries(r.data?.entries || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  };
  const toggle = () => { if (!open) load(); setOpen((v) => !v); };
  const fmt = (v) => (v === null || v === undefined || v === "") ? "—" : String(v);
  const label = (a) => a?.replace(/^commission\.user\./, "").replace(/_/g, " ") || "—";
  return (
    <div className="mt-5" data-testid={`sales-settings-history-${userId}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`sales-settings-history-toggle-${userId}`}
        className="text-[11px] text-zinc-400 hover:text-zinc-100 underline underline-offset-2"
      >
        {open ? "Historie ausblenden" : "Provisions-Historie ansehen"}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-zinc-800 bg-[#0E0E12] p-3 space-y-1.5 text-[12px]">
          {loading ? (
            <div className="text-zinc-500">Lade…</div>
          ) : entries.length === 0 ? (
            <div className="text-zinc-500">Noch keine Änderungen protokolliert.</div>
          ) : entries.map((h) => {
            const when = h.ts ? new Date(h.ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
            return (
              <div key={h.id} className="text-zinc-300 leading-relaxed">
                <span className="font-mono text-zinc-500">{when}</span>
                {" · "}
                <span className="text-zinc-100 font-semibold">{h.actor_name || "System"}</span>
                {" (" + (h.actor_role || "—") + ") setzte "}
                <span className="text-zinc-400">{label(h.action)}</span>: {" "}
                <span className="text-zinc-400">{fmt(h.old_value)}</span>
                {" → "}
                <span className="text-[#00E575] font-semibold">{fmt(h.new_value)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TenantDefaultsCard() {
  const { user: currentUser } = useAuth();
  // Iter88zz5 — Mandanten-Default darf ebenfalls nur Admin/SA ändern
  // (siehe Backend PATCH /tenant-pricing/me). Für Non-Admins bleibt die
  // Karte lesbar, aber Speichern-Button + Inputs sind disabled.
  const canEditTenant = currentUser?.role === "admin" || currentUser?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marginType, setMarginType] = useState("percent");
  const [marginValue, setMarginValue] = useState("");
  const [maxPct, setMaxPct] = useState("");
  const [maxEur, setMaxEur] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/tenant-pricing/me");
      setData(r.data);
      setMarginType(r.data.default_rep_margin_type || "percent");
      setMarginValue(r.data.default_rep_margin_value ?? r.data.legacy_default_rep_margin_pct ?? "");
      setMaxPct(r.data.default_max_discount_percent ?? "");
      setMaxEur(r.data.default_max_discount_eur ?? "");
    } catch {
      toast.error("Konnte Mandanten-Standards nicht laden");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Iter88zbt — Reload wenn irgendwo auf der Seite Provision/Rabatt geändert
  // wurde (z. B. eigene Vertriebler-Override wirkt auf Standard-Anzeige).
  // Iter88zbu — Silent (kein Karten-Content-Flash beim Auto-Reload).
  useReloadBus("*", () => load(true));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        default_rep_margin_type: marginValue === "" || marginValue == null ? null : marginType,
        default_rep_margin_value: marginValue === "" || marginValue == null ? null : parseFloat(marginValue),
        default_max_discount_percent: maxPct === "" || maxPct == null ? null : parseFloat(maxPct),
        default_max_discount_eur: maxEur === "" || maxEur == null ? null : parseFloat(maxEur),
      };
      const r = await api.patch("/tenant-pricing/me", body);
      setData(r.data);
      toast.success("Mandanten-Standard gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-amber-500/25 rounded-2xl p-5 mb-6 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> Lade Mandanten-Standards…
      </div>
    );
  }

  return (
    <div
      className="bg-[#18181B] border border-amber-500/25 rounded-2xl p-5 mb-6"
      data-testid="tenant-defaults-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={18} className="text-amber-300" />
        <div>
          <h2 className="font-display text-lg font-semibold text-amber-100">
            Mandanten-Standard <span className="text-amber-400/70 text-sm font-normal">· {data?.tenant_name || "—"}</span>
          </h2>
          <p className="text-xs text-zinc-400 mt-0.5 leading-snug">
            Gilt automatisch für alle Verkäufer, die weder eine eigene Übersteuerung noch einen Vertriebsleiter-Default haben.
          </p>
        </div>
      </div>

      <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-4" disabled={!canEditTenant} style={{ opacity: canEditTenant ? 1 : 0.65 }} data-testid="tenant-defaults-fieldset">
        <div className="bg-[#0F0F11] border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-bold uppercase tracking-wider text-amber-200">
            <ShieldCheck size={13} /> Marge (Standard)
          </div>
          <div className="flex gap-2 mb-2">
            <button
              data-testid="tenant-margin-type-percent"
              onClick={() => setMarginType("percent")}
              className={`flex-1 h-9 rounded-lg text-xs font-medium border transition flex items-center justify-center gap-1 ${
                marginType === "percent"
                  ? "border-amber-400 bg-amber-500/15 text-amber-200"
                  : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-600"
              }`}
            >
              <Percent size={12} /> Prozent
            </button>
            <button
              data-testid="tenant-margin-type-eur"
              onClick={() => setMarginType("eur")}
              className={`flex-1 h-9 rounded-lg text-xs font-medium border transition flex items-center justify-center gap-1 ${
                marginType === "eur"
                  ? "border-amber-400 bg-amber-500/15 text-amber-200"
                  : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-600"
              }`}
            >
              <Euro size={12} /> Euro
            </button>
          </div>
          <div className="relative">
            <input
              data-testid="tenant-margin-value"
              type="number"
              min="0"
              step={marginType === "percent" ? "0.5" : "10"}
              value={marginValue}
              onChange={(e) => setMarginValue(e.target.value)}
              placeholder={marginType === "percent" ? "z. B. 15" : "z. B. 1500"}
              className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 pr-10 text-sm text-zinc-100 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 text-xs font-medium">
              {marginType === "percent" ? "%" : "€"}
            </span>
          </div>
          <p className="text-[12px] text-zinc-300 mt-1.5 leading-snug">
            {marginType === "percent"
              ? "Marge = Anteil vom Endkundenpreis (nicht Aufschlag). 15 % → Endkundenpreis = Abgabe / 0,85."
              : "Fester Euro-Aufschlag auf den Abgabepreis."}
          </p>
        </div>

        {/* Discount caps */}
        <div className="bg-[#0F0F11] border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-bold uppercase tracking-wider text-amber-200">
            <Info size={13} /> Rabatt-Obergrenze (Standard)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Max. %</label>
              <div className="relative">
                <input
                  data-testid="tenant-max-discount-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={maxPct}
                  onChange={(e) => setMaxPct(e.target.value)}
                  placeholder="z. B. 5"
                  className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-2.5 pr-7 text-sm text-zinc-100 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 text-xs">%</span>
              </div>
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Max. €</label>
              <div className="relative">
                <input
                  data-testid="tenant-max-discount-eur"
                  type="number"
                  min="0"
                  step="50"
                  value={maxEur}
                  onChange={(e) => setMaxEur(e.target.value)}
                  placeholder="z. B. 500"
                  className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-2.5 pr-7 text-sm text-zinc-100 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 text-xs">€</span>
              </div>
            </div>
          </div>
          <p className="text-[12px] text-zinc-300 mt-1.5 leading-snug">
            Der jeweils kleinere Wert gilt als Obergrenze.
          </p>
        </div>
      </fieldset>

      <Button
        data-testid="tenant-defaults-save-btn"
        onClick={save}
        disabled={saving || !canEditTenant}
        title={canEditTenant ? "" : "Nur Admin/Super-Admin kann den Mandanten-Standard ändern"}
        className="mt-4 h-11 w-full bg-amber-400 text-[#09090B] font-semibold rounded-xl hover:bg-amber-300 active:scale-[0.97] transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 className="animate-spin" /> : <><Save size={14} className="mr-2" /> {canEditTenant ? "Mandanten-Standard speichern" : "Nur Ansicht (Admin erforderlich)"}</>}
      </Button>
    </div>
  );
}

export default function SalesSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ownSettings, setOwnSettings] = useState(null);
  const [reps, setReps] = useState([]);
  const [repSettings, setRepSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRep, setExpandedRep] = useState(null);
  // Iter88zz5 — Provisionen dürfen nur noch von Admin/Super-Admin geändert werden.
  const canEdit = user?.role === "admin" || user?.role === "super_admin";

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const my = await api.get(`/users/${user.id}/sales-settings`);
      setOwnSettings(my.data);
      const users = await api.get("/users");
      const myReps = (users.data || []).filter(
        (u) => u.id !== user.id && u.role === "sales_rep"
      );
      setReps(myReps);
      // Iter88zbt-fix — parallele Rep-Loads via Promise.all statt sequentiell
      // (bei 7 Reps: 1 statt 7 sequentielle Requests → deutlich schneller).
      const map = {};
      const settled = await Promise.allSettled(
        myReps.map((r) => api.get(`/users/${r.id}/sales-settings`))
      );
      myReps.forEach((r, i) => {
        const s = settled[i];
        if (s.status === "fulfilled") map[r.id] = s.value.data;
      });
      setRepSettings(map);
    } catch (e) {
      toast.error("Konnte Einstellungen nicht laden");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  // Iter88zbt — Auto-Reload aller Ebenen (eigene + Vertriebler-Overrides)
  // sobald irgendwo auf der Seite gespeichert wurde — SILENT damit kein
  // Full-Page-Spinner aufblitzt und State erhalten bleibt.
  useReloadBus("*", useCallback(() => load(true), [load]));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500">
        <Loader2 className="animate-spin mr-2" /> Lade Einstellungen…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          data-testid="sales-settings-back-btn"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 mb-4"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>

        <h1 className="font-display text-2xl font-semibold mb-1">Provision & Rabatt</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Marge (Anteil vom Endkundenpreis) und Rabatt-Obergrenze pro Angebot — für Mandant, Team und einzelne Vertriebler.
        </p>

        {/* Iter88zat — Effektive Marge (Transparenz) */}
        <EffectiveMarginCard onCleared={load} />

        {/* Tenant defaults — sichtbar für admin / sales_director / super_admin */}
        {(user?.role === "admin" || user?.role === "sales_director" || user?.role === "super_admin") && (
          <>
            <TenantDefaultsCard />
            <AutomationSettingsCard user={user} />
            <DigestRecipientsCard user={user} />
          </>
        )}

        {/* Own defaults */}
        {ownSettings && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-6" data-testid="sales-settings-own">
            <div className="mb-4">
              <h2 className="font-display text-lg font-semibold">Standard für dein Team</h2>
              <p className="text-xs text-zinc-300 mt-0.5">Gilt automatisch für jeden Vertrieb, der keine eigene Übersteuerung hat.</p>
            </div>
            <SettingsForm
              target={ownSettings}
              isOwn={true}
              readOnly={!canEdit}
              hasMultipleTenants={(user?._memberships_count || 0) > 1}
              activeTenantName={user?._active_tenant_name || user?.tenant_name || null}
              onSaved={(d) => setOwnSettings(d)}
            />
          </div>
        )}

        {/* Reps */}
        <h2 className="font-display text-lg font-semibold mb-3">Deine Vertrieb</h2>
        {reps.length === 0 ? (
          <div className="bg-[#18181B] border border-dashed border-zinc-800 rounded-2xl p-8 text-center text-zinc-500">
            Aktuell sind dir keine Vertrieb zugeordnet.
          </div>
        ) : (
          <div className="space-y-2">
            {reps.map((r) => {
              const open = expandedRep === r.id;
              const rs = repSettings[r.id] || {};
              const hasOverride = rs.margin_value != null || rs.max_discount_percent != null || rs.max_discount_eur != null;
              return (
                <div key={r.id} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
                  <button
                    data-testid={`rep-row-${r.id}`}
                    onClick={() => setExpandedRep(open ? null : r.id)}
                    className="w-full text-left p-4 hover:bg-zinc-900/40 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium">{r.first_name} {r.last_name}</div>
                      <div className="text-xs text-zinc-300 font-mono">{r.phone}</div>
                    </div>
                    {hasOverride ? (
                      <span className="px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575]">Eigene Werte</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-400">Standard</span>
                    )}
                  </button>
                  {open && rs.user_id && (
                    <div className="border-t border-zinc-800 p-5">
                      <SettingsForm
                        target={rs}
                        isOwn={false}
                        readOnly={!canEdit}
                        onSaved={(d) => setRepSettings({ ...repSettings, [r.id]: d })}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info card */}
        <div className="mt-8 bg-[#0F1E18] border border-[#00E575]/20 rounded-2xl p-4 text-xs text-zinc-300 leading-relaxed">
          <div className="flex items-start gap-2">
            <Info size={14} className="text-[#00E575] mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-[#00E575] mb-1">So wirkt die Marge</p>
              <p>Die Marge ist der <strong>Anteil vom Endkundenpreis</strong> — nicht ein Aufschlag auf den Abgabepreis. Bei 15 % Marge auf einen Abgabepreis von 12.000 € rechnet die App: <code className="text-emerald-300">Endkundenpreis = 12.000 / (1 − 0,15) = 14.117,65 €</code>. Der Vertrieb sieht und kommuniziert nur den finalen Endkundenpreis. Der Rabatt-Cap (Max. % oder Max. €) wird automatisch durchgesetzt.</p>
              <p className="mt-2">Vererbungskette: <strong>Paket-Override → Vertriebler-Override → Vertriebsleiter/Niederlassungsleiter → Vertriebsdirektor → Niederlassung → Mandanten-Standard</strong>.</p>
            </div>
          </div>
        </div>

        {/* Sample calculation */}
        <div className="mt-3 text-[12px] text-zinc-400 leading-relaxed">
          Beispiel: Abgabepreis {fmtEur(12000)} · Marge 15 % → Endkundenpreis {fmtEur(12000 / 0.85)}. Rabatt-Cap 5 % oder 500 € → {fmtEur(Math.min(14117.65 * 0.05, 500))} (kleinerer Wert) → finales Brutto {fmtEur((14117.65 - Math.min(14117.65 * 0.05, 500)) * 1.19)}.
        </div>
      </div>
    </div>
  );
}

// Iter88zz9r — Automation-Card: aktuell einziger Toggle „AZ2/AZ3 automatisch
// bei Stage-Advance erzeugen". Weitere Automatismen (Zahlungsbestätigung,
// KfW-Trigger …) können hier ergänzt werden.
function AutomationSettingsCard({ user }) {
  const [autoAz, setAutoAz] = useState(true);
  const [pdfLayout, setPdfLayout] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/tenants/self/settings")
      .then((r) => {
        setAutoAz(r.data?.settings?.auto_create_az_on_stage_advance !== false);
        setPdfLayout(Number(r.data?.settings?.pdf_layout_version || 1));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/tenants/self/settings/auto-az", { enabled: next });
      setAutoAz(next);
      toast.success(next ? "Auto-AZ aktiviert" : "Auto-AZ deaktiviert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const saveLayout = async (nextChecked) => {
    const version = nextChecked ? 2 : 1;
    setSaving(true);
    try {
      await api.patch("/tenants/self/settings/pdf-layout", { version });
      setPdfLayout(version);
      toast.success(`PDF-Layout ${version} aktiviert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-6" data-testid="automation-settings-card">
      <div className="mb-4">
        <h2 className="font-display text-lg font-semibold">Automatisierung</h2>
        <p className="text-xs text-zinc-300 mt-0.5">Legt fest, wann das System eigenständig Aktionen ausführt.</p>
      </div>
      <label className="flex items-start gap-3 p-3 rounded-xl bg-[#09090B] border border-zinc-800 cursor-pointer hover:border-[#00E575]/40 transition">
        <input
          type="checkbox"
          checked={autoAz}
          onChange={(e) => save(e.target.checked)}
          disabled={loading || saving}
          data-testid="toggle-auto-az"
          className="mt-1 h-4 w-4 accent-[#00E575]"
        />
        <div className="flex-1">
          <div className="text-[13.5px] font-semibold text-zinc-100">
            2. + 3. Anzahlungsrechnung automatisch erzeugen
          </div>
          <div className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">
            Wenn ein Projekt auf <b>Montagebeginn</b> gesetzt wird, erstellt das System automatisch die
            2. Anzahlungsrechnung (30 %). Bei <b>Inbetriebnahme</b> wird die 3. Anzahlungsrechnung
            (Restbetrag) erstellt. Deaktiviere diesen Schalter, wenn dein Team die Rechnungen
            manuell prüfen möchte, bevor sie gestellt werden.
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5">
            Status: <b className={autoAz ? "text-[#00E575]" : "text-amber-300"}>
              {loading ? "…" : autoAz ? "aktiv" : "deaktiviert"}
            </b>
          </div>
        </div>
      </label>

      {/* Iter88zz9y — PDF-Layout-Toggle (Layout 1 = Standard, Layout 2 = Redesign). */}
      <label className="mt-3 flex items-start gap-3 p-3 rounded-xl bg-[#09090B] border border-zinc-800 cursor-pointer hover:border-[#00E575]/40 transition">
        <input
          type="checkbox"
          checked={pdfLayout === 2}
          onChange={(e) => saveLayout(e.target.checked)}
          disabled={loading || saving}
          data-testid="toggle-pdf-layout2"
          className="mt-1 h-4 w-4 accent-[#00E575]"
        />
        <div className="flex-1">
          <div className="text-[13.5px] font-semibold text-zinc-100">
            PDF-Layout 2 (Redesign) verwenden
          </div>
          <div className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">
            Aktiviert das modernisierte Angebots-Layout mit <b>KPI-Cards</b> für
            Wirtschaftlichkeit, <b>Foto-Icons</b> in der Positionstabelle (sobald
            Komponenten-Bilder gepflegt sind) und einem <b>kompakteren
            15-Jahres-Chart</b>. Deaktiviert nutzt das System das klassische
            tabellarische Layout (Standard).
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5">
            Aktive Version: <b className="text-[#00E575]">
              {loading ? "…" : `Layout ${pdfLayout}`}
            </b>
          </div>
        </div>
      </label>
    </div>
  );
}


// Iter88zz9a6 (2026-09-07) — Digest-Empfänger-Editor.
function DigestRecipientsCard({ user }) {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/tenants/self/settings/digest-recipients")
      .then((r) => setEmails(r.data?.emails || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await api.patch("/tenants/self/settings/digest-recipients", { emails: next });
      setEmails(r.data?.emails || []);
      toast.success("Digest-Empfänger aktualisiert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const addEmail = () => {
    const e = (input || "").trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Bitte eine gültige E-Mail eingeben"); return; }
    if (emails.includes(e)) { toast.warning("E-Mail steht bereits in der Liste"); return; }
    setInput("");
    persist([...emails, e]);
  };
  const removeEmail = (e) => persist(emails.filter((x) => x !== e));
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-6" data-testid="digest-recipients-card">
      <div className="mb-4">
        <h2 className="font-display text-lg font-semibold">Tagesübersicht — Empfänger</h2>
        <p className="text-xs text-zinc-300 mt-0.5">
          E-Mails, an die die wöchentliche Pipeline-Pulse-Mail geht.
          {emails.length === 0
            ? " Aktuell leer — automatisch werden alle Admins + Sales-Direktoren adressiert."
            : " Sobald hier Adressen stehen, wird ausschließlich diese Liste genutzt."}
        </p>
      </div>
      <div className="flex gap-2 mb-3">
        <input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addEmail()}
          placeholder="empfaenger@beispiel.de"
          data-testid="digest-add-email-input"
          className="flex-1 h-10 px-3 rounded-lg bg-[#09090B] border border-zinc-700 text-zinc-100 text-sm"
          disabled={loading || saving}
        />
        <button
          onClick={addEmail}
          disabled={loading || saving || !input.trim()}
          data-testid="digest-add-email-btn"
          className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] disabled:opacity-40"
        >
          Hinzufügen
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-zinc-400">Lade …</p>
      ) : emails.length === 0 ? (
        <p className="text-xs text-zinc-500 italic">
          Fallback aktiv: alle Admins + Sales-Direktoren erhalten die Mail automatisch.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {emails.map((e) => (
            <li key={e} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#09090B] border border-zinc-800">
              <span className="text-sm text-zinc-200 font-mono truncate">{e}</span>
              <button
                onClick={() => removeEmail(e)}
                disabled={saving}
                data-testid={`digest-remove-${e.replace(/[^a-z0-9]/gi,'-')}`}
                className="text-[11px] text-red-400 hover:text-red-300 font-bold px-2 py-1 disabled:opacity-40"
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
